<template>
    <LoaderComponent v-if="sites.length === 0 && isLoading" class="loader" />
    <table v-else class="site-table">
        <thead>
            <tr>
                <th class="checkbox" />
                <th class="column-site">
                    <span>
                        {{ $t('siteSelection.table.site') }}
                    </span>
                </th>
                <th class="column-address">
                    <span>
                        {{ $t('siteSelection.table.address') }}
                    </span>
                </th>
            </tr>
        </thead>
        <tbody v-if="sites.length > 0">
            <tr v-for="site in sites" :key="site.codeSite" class="pointer" v-on:click="toggleSelect(site)">
                <td v-if="!isMultiSite">
                    <RadioBox :selected="site.id === selectedSite?.id" class="radio" />
                </td>
                <td v-else>
                    <CheckBox :checked="siteSelected(site.id)" class="checkbox" label="" />
                </td>
                <td>
                    <div class="site-description">{{ site.description }}</div>
                </td>
                <td>
                    {{ formatAddress(site) }}
                </td>
            </tr>
            <tr v-if="isLoading">
                <td :colspan="3" class="noDataTd">
                    {{ $t('search.isLoading') }}
                </td>
            </tr>
        </tbody>
        <tbody v-else>
            <tr>
                <td class="notFoundTd" colspan="3">
                    {{ $t('search.notFound') }}
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script lang="ts">
import { Site } from '@/models/Site';
import RadioBox from '@/components/ui-components/RadioBox.vue';
import { defineComponent, PropType } from 'vue';
import LoaderComponent from '@/components/ui-components/LoaderComponent.vue';
import CheckBox from '@/components/ui-components/CheckBox.vue';

export default defineComponent({
    name: 'SitesList',
    props: {
        sites: { type: Object, required: true },
        selectedSite: { type: Object as PropType<Site> },
        selectedSites: { type: Array as PropType<Site[]> },
        isLoading: { type: Boolean, required: true },
        sitesDisabled: { type: Boolean, required: false },
        isMultiSite: { type: Boolean, required: false },
    },
    components: {
        CheckBox,
        RadioBox,
        LoaderComponent,
    },
    methods: {
        toggleSelect(site: Site) {
            if (this.sitesDisabled) return;

            if (this.isMultiSite) {
                const alreadySelected = this.selectedSites?.some(s => s.id === site.id);
                if (!alreadySelected) {
                    const newSelection = this.selectedSites ? [...this.selectedSites, site] : [site];
                    this.$emit('add', newSelection);
                }
            } else {
                this.$emit('pick', site);
            }
        },
        formatAddress(site: Site) {
            const values = [
                site.numero,
                site.natureVoie,
                site.nomVoie,
                site.codePostal,
                site.bureauDistributeur,
            ].filter(v => v !== undefined);
            return values.join(' ');
        },
        siteSelected(siteId: number): boolean {
            return this.selectedSites?.find(site => site.id === siteId) != null;
        },
    },
});
</script>

<style scoped>
.site-table {
    width: 100%;
    border-collapse: separate;
    border-spacing: 0;
    border-right: 1px solid var(--medium-dark-grey);
    border-left: 1px solid var(--medium-dark-grey);
    border-bottom: 1px solid var(--medium-dark-grey);
    text-align: left;
}

.site-table thead {
    position: sticky;
    top: 0;
    font-size: 14px;
    color: var(--dark-grey);
    font-family: 'Raleway Semi Bold', sans-serif !important;
    font-variant-numeric: lining-nums;
    font-weight: 600;
    z-index: 1;
}

.site-table thead th {
    box-sizing: border-box;
    padding: 0;
    position: sticky;
    top: 0;
    border-bottom: 1px solid var(--medium-dark-grey);
    border-top: 1px solid var(--medium-dark-grey);
    background-color: white;
    height: 52px;
}

.checkbox {
    width: 4%;
}
.column-site {
    width: 48%;
}
.column-address {
    width: 48%;
}

.site-table tr td {
    border-bottom: 1px solid var(--medium-dark-grey);
    height: 52px;
}

.site-table tbody tr:last-child td {
    border: none;
}

.site-table thead tr:first-child {
    border-top: none;
}

.site-table tr td {
    box-sizing: border-box;
    padding: 0 5px;
}

.radio {
    margin-top: 2px;
    margin-left: 32px;
}
.notFoundTd {
    text-align: center;
    height: 25vh;
}

.pointer {
    cursor: pointer;
}

.noDataTd {
    text-align: center;
}
.checkbox {
    margin-bottom: 20px;
    margin-left: 32px;
}
</style>
